import { AlertTriangle, UsersRound } from 'lucide-react'
import GenerationBasisSummary from '@/components/generationBasis/GenerationBasisSummary'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { getAudienceKindLabel } from '@/utils/audience-builder'

/** Warning shown next to the chip when some interests of the audience are no longer available on Meta. */
const StaleWarning = ({ note }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span role="img" aria-label={note} className="tw:shrink-0" data-testid="hypothesis-stale-warning">
                <AlertTriangle size={16} className="tw:text-amber-400" />
            </span>
        </TooltipTrigger>
        <TooltipContent className="tw:max-w-80">
            <span>{note}</span>
        </TooltipContent>
    </Tooltip>
)

/** Marker shown next to the chip when the ad set also targets behaviours or demographics, naming them. */
const TargetingOptionMarker = ({ note }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span
                role="img"
                aria-label={`Also targets behaviours and demographics: ${note}`}
                className="tw:shrink-0"
                data-testid="hypothesis-targeting-options"
            >
                <UsersRound size={16} className="tw:text-green-300" />
            </span>
        </TooltipTrigger>
        <TooltipContent className="tw:max-w-80">
            <span>Also targets behaviours and demographics (in the same group as the interests): {note}</span>
        </TooltipContent>
    </Tooltip>
)

/**
 * Kind and stored label of the AI audience an ad set was created from, with what the audience was based on (from the
 * stored record, collapsed). Nothing for other ad sets.
 */
const HypothesisChip = ({ hypothesis, className = '' }) => {
    if (!hypothesis?.kind) return null

    const kindLabel = getAudienceKindLabel(hypothesis.kind)
    const label = hypothesis.label && hypothesis.label !== kindLabel ? hypothesis.label : null
    const text = label ? `${kindLabel} · ${label}` : kindLabel

    const chip = (
        <span
            data-testid="hypothesis-chip"
            title={text}
            className={cn(
                'tw:inline-block tw:max-w-[220px] tw:truncate tw:rounded-full tw:border tw:border-blue-300/60 tw:bg-blue-300/10 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-blue-300',
                className,
            )}
        >
            {text}
        </span>
    )

    if (!hypothesis.stale_note && !hypothesis.targeting_option_note && !hypothesis.basis) return chip

    return (
        <>
            {chip}
            <GenerationBasisSummary basis={hypothesis.basis} popover />
            {hypothesis.targeting_option_note && <TargetingOptionMarker note={hypothesis.targeting_option_note} />}
            {hypothesis.stale_note && <StaleWarning note={hypothesis.stale_note} />}
        </>
    )
}

export default HypothesisChip
